import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useFieldErrorMessage } from './useFieldErrorMessage';
import { UserTransferListbox, type UserTransferListboxProps } from '../UserTransferListbox';

export interface FormUserTransferListboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<UserTransferListboxProps, 'value' | 'onChange' | 'error' | 'name'> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Default value for the field
   */
  defaultValue?: string[];
}

/**
 * React Hook Form adapter for UserTransferListbox
 * Automatically handles form state, validation, and errors
 *
 * @example
 * ```tsx
 * const { control } = useForm<FormData>();
 *
 * <FormUserTransferListbox
 *   control={control}
 *   name="departmentMembers"
 *   label="Department Members"
 *   description="Select users to assign to this department"
 *   required
 * />
 * ```
 */
export function FormUserTransferListbox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  defaultValue = [],
  ...fieldProps
}: FormUserTransferListboxProps<TFieldValues, TName>) {
  const fieldErrorMessage = useFieldErrorMessage();

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue as never}
      render={({ field, fieldState }) => (
        <UserTransferListbox
          {...fieldProps}
          id={String(name)}
          name={String(name)}
          value={(field.value as string[]) || []}
          onChange={field.onChange}
          error={fieldErrorMessage(fieldState.error)}
        />
      )}
    />
  );
}

FormUserTransferListbox.displayName = 'FormUserTransferListbox';
